// services/e2eService.js - Chiffrement E2E avec Perfect Forward Secrecy
// Protocole : ECDH P-256 éphémère + AES-GCM 256 + HKDF

const E2E = {

  // Générer une paire de clés ECDH P-256 (identité long terme)
  async generateKeyPair() {
    const keyPair = await window.crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveKey', 'deriveBits']
    );
    const publicKey = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
    const privateKey = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
    return {
      publicKey: btoa(String.fromCharCode(...new Uint8Array(publicKey))),
      privateKey: btoa(String.fromCharCode(...new Uint8Array(privateKey))),
    };
  },

  // Importer clé publique ECDH
  async importPublicKey(base64Key) {
    const binaryKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
    return window.crypto.subtle.importKey(
      'spki', binaryKey,
      { name: 'ECDH', namedCurve: 'P-256' },
      false, []
    );
  },

  // Importer clé privée ECDH
  async importPrivateKey(base64Key) {
    const binaryKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
    return window.crypto.subtle.importKey(
      'pkcs8', binaryKey,
      { name: 'ECDH', namedCurve: 'P-256' },
      false, ['deriveKey', 'deriveBits']
    );
  },

  // Dériver une clé AES-GCM via ECDH + HKDF
  async deriveAESKey(privateKey, publicKey, salt) {
    const sharedBits = await window.crypto.subtle.deriveBits(
      { name: 'ECDH', public: publicKey },
      privateKey,
      256
    );
    const hkdfKey = await window.crypto.subtle.importKey(
      'raw', sharedBits, { name: 'HKDF' }, false, ['deriveKey']
    );
    return window.crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode('PayChat E2E v2') },
      hkdfKey,
      { name: 'AES-GCM', length: 256 },
      false, ['encrypt', 'decrypt']
    );
  },

  // Chiffrer un message avec PFS
  async encrypt(message, recipientPublicKeyB64) {
    try {
      // 1. Clé publique long terme du destinataire
      const recipientPubKey = await this.importPublicKey(recipientPublicKeyB64);

      // 2. Générer une paire éphémère (PFS - jetée après)
      const ephemeralPair = await window.crypto.subtle.generateKey(
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        ['deriveKey', 'deriveBits']
      );
      const ephemeralPubExported = await window.crypto.subtle.exportKey('spki', ephemeralPair.publicKey);
      const ephemeralPubB64 = btoa(String.fromCharCode(...new Uint8Array(ephemeralPubExported)));

      // 3. ECDH : clé éphémère privée × clé publique destinataire → secret partagé
      const salt = window.crypto.getRandomValues(new Uint8Array(32));
      const aesKey = await this.deriveAESKey(ephemeralPair.privateKey, recipientPubKey, salt);

      // 4. Chiffrer le message
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encoded = new TextEncoder().encode(message);
      const encryptedMsg = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv }, aesKey, encoded
      );

      return JSON.stringify({
        v: 2,
        eph: ephemeralPubB64,
        salt: btoa(String.fromCharCode(...salt)),
        iv: btoa(String.fromCharCode(...iv)),
        msg: btoa(String.fromCharCode(...new Uint8Array(encryptedMsg))),
      });
    } catch(e) {
      console.error('E2E encrypt error:', e);
      return message;
    }
  },

  // Déchiffrer un message avec PFS
  async decrypt(encryptedData, privateKeyB64) {
    try {
      const parsed = JSON.parse(encryptedData);

      // Compatibilité avec l'ancien format v1 (RSA)
      if (parsed.v === 1) {
        return this._decryptV1(encryptedData, privateKeyB64);
      }

      if (parsed.v !== 2) return encryptedData;
      const { eph, salt, iv, msg } = parsed;

      // 1. Importer la clé privée long terme du destinataire
      const privateKey = await this.importPrivateKey(privateKeyB64);

      // 2. Importer la clé publique éphémère de l'expéditeur
      const ephPublicKey = await this.importPublicKey(eph);

      // 3. ECDH : clé privée destinataire × clé éphémère publique → même secret
      const saltBytes = Uint8Array.from(atob(salt), c => c.charCodeAt(0));
      const aesKey = await this.deriveAESKey(privateKey, ephPublicKey, saltBytes);

      // 4. Déchiffrer
      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: Uint8Array.from(atob(iv), c => c.charCodeAt(0)) },
        aesKey,
        Uint8Array.from(atob(msg), c => c.charCodeAt(0))
      );
      return new TextDecoder().decode(decrypted);
    } catch(e) {
      return encryptedData;
    }
  },

  // Compatibilité déchiffrement v1 (RSA-OAEP)
  async _decryptV1(encryptedData, privateKeyB64) {
    try {
      const { key, iv, msg } = JSON.parse(encryptedData);
      const binaryKey = Uint8Array.from(atob(privateKeyB64), c => c.charCodeAt(0));
      const privateKey = await window.crypto.subtle.importKey(
        'pkcs8', binaryKey,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false, ['decrypt']
      );
      const encryptedAES = Uint8Array.from(atob(key), c => c.charCodeAt(0));
      const aesKeyData = await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, encryptedAES);
      const aesKey = await window.crypto.subtle.importKey('raw', aesKeyData, { name: 'AES-GCM' }, false, ['decrypt']);
      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: Uint8Array.from(atob(iv), c => c.charCodeAt(0)) },
        aesKey,
        Uint8Array.from(atob(msg), c => c.charCodeAt(0))
      );
      return new TextDecoder().decode(decrypted);
    } catch(e) { return encryptedData; }
  },

  // Initialiser E2E - regénérer si ancien format RSA
  async init(userId) {
    const storageKey = `e2e_private_${userId}`;
    let privateKey = localStorage.getItem(storageKey);
    let publicKey = localStorage.getItem(`e2e_public_${userId}`);
    const version = localStorage.getItem(`e2e_version_${userId}`);

    // Regénérer si pas de clés ou version ancienne (RSA → ECDH)
    if (!privateKey || !publicKey || version !== 'v2') {
      const keyPair = await this.generateKeyPair();
      privateKey = keyPair.privateKey;
      publicKey = keyPair.publicKey;
      localStorage.setItem(storageKey, privateKey);
      localStorage.setItem(`e2e_public_${userId}`, publicKey);
      localStorage.setItem(`e2e_version_${userId}`, 'v2');
    }
    return { privateKey, publicKey };
  },

  getPrivateKey(userId) {
    return localStorage.getItem(`e2e_private_${userId}`);
  },

  isEncrypted(content) {
    try {
      const parsed = JSON.parse(content);
      return (parsed.v === 1 && parsed.key && parsed.iv && parsed.msg) ||
             (parsed.v === 2 && parsed.eph && parsed.iv && parsed.msg);
    } catch { return false; }
  }
};

export default E2E;
